import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Building2, TrendingUp, Users, Check, Clock, FileText } from 'lucide-react';
import { Button } from '../components/Button';
export function Services() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth'
        });
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);
  const ServiceSection = ({
    id,
    title,
    code,
    description,
    icon: Icon,
    features,
    idealFor,
    deliverables,
    timeline,
    requirements
  }: any) => <section id={id} className="py-20 border-b border-gray-100 last:border-0" data-id="element-460">
      <div className="container-custom" data-id="element-461">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-462">
          {/* Left Column: Header & Description */}
          <div className="lg:col-span-4" data-id="element-463">
            <div className="bg-blue-50 w-16 h-16 rounded-xl flex items-center justify-center mb-6" data-id="element-464">
              <Icon className="h-8 w-8 text-blue-800" data-id="element-465" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-466">{title}</h2>
            <p className="text-sm font-mono text-gray-500 mb-6" data-id="element-467">
              Activity Code: {code}
            </p>
            <p className="text-gray-600 mb-8 leading-relaxed" data-id="element-468">{description}</p>
            <Link to="/contact" data-id="element-469">
              <Button className="w-full sm:w-auto" data-id="element-470">Request a Proposal</Button>
            </Link>
          </div>

          {/* Right Column: Details */}
          <div className="lg:col-span-8" data-id="element-471">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8" data-id="element-472">
              {/* Key Services */}
              <div className="mb-10" data-id="element-473">
                <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-474">
                  Key Services
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-475">
                  {features.map((feature: string, idx: number) => <div key={idx} className="flex items-start" data-id="element-476">
                      <Check className="h-5 w-5 text-green-500 mr-3 mt-0.5 shrink-0" data-id="element-477" />
                      <span className="text-gray-700" data-id="element-478">{feature}</span>
                    </div>)}
                </div>
              </div>

              {/* Grid Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-479">
                <div data-id="element-480">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center" data-id="element-481">
                    <Users className="h-4 w-4 mr-2 text-blue-600" data-id="element-482" /> Ideal For
                  </h4>
                  <ul className="space-y-2" data-id="element-483">
                    {idealFor.map((item: string, idx: number) => <li key={idx} className="text-sm text-gray-600 flex items-start" data-id="element-484">
                        <span className="w-1.5 h-1.5 bg-gray-300 rounded-full mr-2 mt-1.5" data-id="element-485"></span>
                        {item}
                      </li>)}
                  </ul>
                </div>

                <div data-id="element-486">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center" data-id="element-487">
                    <FileText className="h-4 w-4 mr-2 text-blue-600" data-id="element-488" />{' '}
                    Deliverables
                  </h4>
                  <ul className="space-y-2" data-id="element-489">
                    {deliverables.map((item: string, idx: number) => <li key={idx} className="text-sm text-gray-600 flex items-start" data-id="element-490">
                        <span className="w-1.5 h-1.5 bg-gray-300 rounded-full mr-2 mt-1.5" data-id="element-491"></span>
                        {item}
                      </li>)}
                  </ul>
                </div>

                <div data-id="element-492">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center" data-id="element-493">
                    <Clock className="h-4 w-4 mr-2 text-blue-600" data-id="element-494" /> Typical
                    Timeline
                  </h4>
                  <p className="text-sm text-gray-600" data-id="element-495">{timeline}</p>
                </div>

                <div data-id="element-496">
                  <h4 className="font-semibold text-gray-900 mb-3" data-id="element-497">
                    What we need from you
                  </h4>
                  <p className="text-sm text-gray-600" data-id="element-498">{requirements}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
  return <div className="pt-10" data-id="element-499">
      <div className="bg-gray-50 py-16" data-id="element-500">
        <div className="container-custom text-center" data-id="element-501">
          <h1 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-502">
            Our Services
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-503">
            Comprehensive corporate solutions tailored to your business needs in
            the UAE.
          </p>
        </div>
      </div>

      <ServiceSection id="corporate" title="Corporate Services Provider" code="7010006" description="We handle the complexities of company formation and structuring, ensuring your business is set up correctly from day one. Our team navigates the regulatory landscape so you can focus on your core business." icon={Building2} features={['Company structuring & setup coordination', 'Corporate documentation support', 'Compliance support coordination', 'Office / flexi-desk coordination support']} idealFor={['New market entrants', 'International subsidiaries', 'Freelancers upgrading to trade licenses', 'Holding companies']} deliverables={['Trade License', 'Memorandum of Association', 'Establishment Card', 'Lease Agreement']} timeline="5-10 working days (subject to approvals)" requirements="Passport copies, photos, and basic business details." data-id="element-504" />

      <ServiceSection id="investment" title="Investment in Commercial Enterprises & Management" code="6499001" description="For investors and holding companies, we provide strategic management support and due diligence coordination. We help you oversee your portfolio and ensure operational efficiency." icon={TrendingUp} features={['Commercial opportunity sourcing support', 'Business operations & performance consulting', 'Due diligence support coordination', 'Post-investment management support']} idealFor={['Angel investors', 'Family offices', 'Investment holding companies', 'Serial entrepreneurs']} deliverables={['Opportunity assessment reports', 'Operational KPI dashboards', 'Management review meetings', 'Strategic roadmaps']} timeline="Ongoing engagement or project-based" requirements="Investment criteria, portfolio details, and strategic goals." data-id="element-505" />

      <ServiceSection id="brokerage" title="Commercial Brokers" code="4610010" description="We act as a bridge between businesses, facilitating commercial introductions and deals. Our network helps you find the right partners, suppliers, and clients to grow your business." icon={Users} features={['Commercial introductions', 'Deal facilitation support', 'Negotiation support', 'Vendor and partner screening process']} idealFor={['B2B service providers', 'Trading companies', 'Businesses seeking local partners', 'Distributors']} deliverables={['Qualified introductions', 'Meeting coordination', 'Deal term sheets (drafting support)', 'Partner assessment summaries']} timeline="Variable based on deal complexity" requirements="Target partner profile, value proposition, and commercial terms." data-id="element-506" />

      <section className="py-20 bg-blue-900 text-white text-center" data-id="element-507">
        <div className="container-custom" data-id="element-508">
          <h2 className="text-3xl font-bold mb-6" data-id="element-509">
            Not sure which service you need?
          </h2>
          <p className="text-blue-100 mb-8 max-w-2xl mx-auto" data-id="element-510">
            Our team can assess your business model and recommend the most
            suitable activity codes and structure.
          </p>
          <Link to="/contact" data-id="element-511">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100 border-none" data-id="element-512">
              Speak to an Advisor
            </Button>
          </Link>
        </div>
      </section>
    </div>;
}